import { useParams, Link } from "react-router-dom";
import SEO from "@/components/base/SEO";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import { articles, allManhwa, categories } from "@/mocks/manhwa";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);
  const manhwa = allManhwa.find((m) => m.id === slug);

  const isManhwa = !!manhwa;
  const content = article || manhwa;

  if (!content) {
    return (
      <>
        <SEO
          title="页面未找到 - 韩漫漫画网"
          description="抱歉，您访问的页面不存在。请返回韩漫漫画网首页浏览更多内容。"
          keywords="韩漫漫画网,免费韩漫,在线漫画"
        />
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">
              页面未找到
            </h1>
            <p className="text-sm text-foreground-500 mb-6">
              您访问的内容可能已被移除或地址不正确
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-1 bg-primary-500 text-white text-sm font-medium px-6 py-2.5 rounded-md hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const pageTitle = `${"title" in content ? content.title : ""} - 韩漫漫画网`;
  const pageDesc =
    "title" in content && "excerpt" in content
      ? content.excerpt
      : "description" in content
        ? (content as { description: string }).description
        : "";
  const pageKeywords = `${"title" in content ? content.title : ""},韩漫漫画网,免费韩漫,在线漫画,韩国漫画`;

  const breadcrumbItems = isManhwa
    ? [
        { label: "首页", to: "/" },
        {
          label:
            categories.find((c) => c.slug === (manhwa as { category: string }).category)?.name || "分类",
          to: `/category/${(manhwa as { category: string }).category}`,
        },
        { label: (manhwa as { title: string }).title },
      ]
    : [
        { label: "首页", to: "/" },
        { label: "文章" },
        { label: "title" in content ? content.title : "" },
      ];

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "title" in content ? content.title : "",
    description: pageDesc,
    author: {
      "@type": "Person",
      name: "author" in content ? (content as { author: string }).author : "韩漫漫画网",
    },
    datePublished: "publishDate" in content ? (content as { publishDate: string }).publishDate : (content as { updatedAt?: string }).updatedAt || "",
    dateModified: "publishDate" in content ? (content as { publishDate: string }).publishDate : (content as { updatedAt?: string }).updatedAt || "",
    publisher: {
      "@type": "Organization",
      name: "韩漫漫画网",
    },
  };

  if (isManhwa) {
    return (
      <>
        <SEO
          title={pageTitle}
          description={pageDesc}
          keywords={pageKeywords}
          ogType="article"
          ogImage={(manhwa as { cover: string }).cover}
          canonicalUrl={typeof window !== "undefined" ? `${window.location.origin}/article/${slug}` : ""}
        />
        <ScrollToTop />
        <Navbar />

        <main className="pt-20">
          <Breadcrumb items={breadcrumbItems} />

          <article className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="w-full md:w-64 flex-shrink-0">
                  <div className="w-full max-w-[240px] mx-auto aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                    <img
                      src={(manhwa as { cover: string }).cover}
                      alt={(manhwa as { title: string }).title}
                      title={`${(manhwa as { title: string }).title} - 免费韩漫在线阅读`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    {(manhwa as { tags: string[] }).tags.map((tag: string) => (
                      <span
                        key={tag}
                        className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                    {(manhwa as { title: string }).title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-600 mb-5">
                    <span>作者：{(manhwa as { author: string }).author}</span>
                    <span className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500"></i>
                      </div>
                      {(manhwa as { rating: number }).rating}分
                    </span>
                    <span>{(manhwa as { views: number }).views.toLocaleString()}次阅读</span>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 mb-5">
                    <span
                      className={`text-xs font-medium px-3 py-1 rounded-full ${
                        (manhwa as { status: string }).status === "连载中"
                          ? "bg-accent-100 text-accent-700"
                          : "bg-secondary-100 text-secondary-700"
                      }`}
                    >
                      {(manhwa as { status: string }).status}
                    </span>
                    <span className="text-xs text-foreground-500">
                      共{(manhwa as { chapters: number }).chapters}话
                    </span>
                    <span className="text-xs text-foreground-500">
                      更新于{"updatedAt" in content ? (content as { updatedAt: string }).updatedAt : ""}
                    </span>
                  </div>

                  <div className="bg-background-100 rounded-lg p-5 mb-5">
                    <h2 className="font-heading text-base font-bold text-foreground-950 mb-2">
                      作品简介
                    </h2>
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      {"description" in content ? (content as { description: string }).description : ""}
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button className="bg-primary-500 text-white text-sm font-medium px-8 py-3 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                      开始阅读
                    </button>
                    <button className="border border-background-300 text-foreground-700 text-sm font-medium px-8 py-3 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
                      加入收藏
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </article>
        </main>

        <Footer />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
        />
      </>
    );
  }

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        ogType="article"
        ogImage={(article as { cover: string })?.cover}
        canonicalUrl={typeof window !== "undefined" ? `${window.location.origin}/article/${slug}` : ""}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <Breadcrumb items={breadcrumbItems} />

        <article className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="mb-8">
              <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500 mb-3">
                <span className="bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded">
                  {(article as { category: string }).category === "news"
                    ? "韩漫资讯"
                    : (article as { category: string }).category === "analysis"
                      ? "深度分析"
                      : "阅读指南"}
                </span>
                <span>{(article as { publishDate: string }).publishDate}</span>
                <span>· {(article as { readTime: number }).readTime}分钟阅读</span>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                {(article as { title: string }).title}
              </h1>
              <p className="text-sm text-foreground-600">
                作者：{(article as { author: string }).author}
              </p>
            </div>

            <div className="w-full aspect-video rounded-lg overflow-hidden mb-8 bg-background-200">
              <img
                src={(article as { cover: string }).cover}
                alt={(article as { title: string }).title}
                title={(article as { title: string }).title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                [&_h2]:font-heading [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4
                [&_h3]:font-heading [&_h3]:text-lg [&_h3]:font-bold [&_h3]:text-foreground-900 [&_h3]:mt-6 [&_h3]:mb-3
                [&_p]:mb-4 [&_p]:leading-relaxed
                [&_strong]:text-foreground-950
                [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-4
                [&_li]:mb-1"
              dangerouslySetInnerHTML={{
                __html: (article as { content: string }).content,
              }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2">
                {(article as { tags: string[] }).tags.map((tag: string) => (
                  <span
                    key={tag}
                    className="text-xs bg-secondary-100 text-secondary-700 px-3 py-1 rounded cursor-pointer hover:bg-secondary-200 transition-colors"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
    </>
  );
}